import { PageEvents } from '@griply/types';
import {
  buildShowCompletedGoalsUpdate,
  getShowCompletedGoals,
  GoalCompletedContext,
} from '@griply/utils';
import { useLifeAreaById } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';

import { GoalsOverview } from './components/goals-overview';
import * as Styled from './life-area-detail.style';

export const Goals: React.FC = () => {
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailGoals, undefined);

  const { id } = useParams();
  const lifeArea = useLifeAreaById(id);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const openCreateGoalDialog = useOpenCreateGoalDialog();

  const showCompletedGoals = getShowCompletedGoals(
    user?.settings,
    GoalCompletedContext.LifeAreaDetail,
  );

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings(
      buildShowCompletedGoalsUpdate(GoalCompletedContext.LifeAreaDetail, value),
    );
  };

  const onAddGoal = () => {
    if (!lifeArea) {
      return;
    }
    openCreateGoalDialog({ lifeAreaId: lifeArea.id });
  };

  if (!lifeArea) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.sections.goals.label')}
      fullSize
      noPadding
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSortMenuOpen(true)}
          />
          <IconButton icon={Plus} onClick={onAddGoal} />
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={() => setIsSortMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
    >
      <GoalsOverview
        lifeArea={lifeArea}
        hideCompleted={!showCompletedGoals}
        fullSize
      />
    </PageSectionBase>
  );
};
